/**
 * Data transformation utilities for Platform Details Dashboard
 * Transforms raw API data into the format expected by the DataTable component
 */

// Vision UI Dashboard PRO React components
import VuiBox from 'components/VuiBox';
import VuiTypography from 'components/VuiTypography';
import VuiBadge from 'components/VuiBadge';

// React Router
import { Link } from 'react-router-dom';

/**
 * Transform raw platform API data into DataTable format
 * @param {Object} apiResponse - The response from the platform API
 * @returns {Object} - DataTable compatible format
 */
export const transformPlatformData = (apiResponse) => {
  const { data, pagination, filters, applied_filters, sorting, platform_stats } = apiResponse;

  // Transform the data array
  const transformedData = data.map((item) => ({
    takedownId: (
      <Link to={`/dashboards/takedowns/${item.id}/details`} style={{ textDecoration: 'none', color:"white"}}>
        <VuiTypography
          variant="button"
          color="white"
          fontWeight="medium"
          sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          {item.id}
        </VuiTypography>
      </Link>
      
    ),

    infringementType: (
    
      <VuiTypography variant="button" color="white" fontWeight="regular">
        {item.violation_type || 'Unknown'}
      </VuiTypography>
      
    ),

    mediaType: (
      <VuiTypography variant="button" color="white" fontWeight="regular">
        {item.media_type || 'N/A'}
      </VuiTypography>
    ),

    priority: (
      <VuiTypography variant="button" color="white" fontWeight="regular">
        {item.violation_type === 'Explicit' ? 'High' : 'Medium'}
      </VuiTypography>
    ),

    priorityReason: (
      <VuiTypography variant="button" color="white" fontWeight="regular">
        {item.violation_type === 'Explicit' ? 'Explicit content at priority platform' : 'Standard violation'}
      </VuiTypography>
    ),

    status: (
      <VuiBox display="flex" alignItems="center" gap={1}>
        <VuiBox 
          width="8px" 
          height="8px" 
          borderRadius="50%" 
          bgColor={
            item.takedown_status === 'Removed' ? 'success' :
            item.takedown_status === 'Pending' ? 'warning' :
            item.takedown_status === 'Submitted' ? 'info' : 'error'
          }
        />
        <VuiTypography variant="button" color="white" fontWeight="regular">
          {item.takedown_status || 'Unknown'}
        </VuiTypography>
      </VuiBox>
    ),

    threatActor: (() => {
      const username = item.username;
      const isAnonymousOrNull = !username || 
        username.toLowerCase() === 'null' || 
        username.toLowerCase() === 'n/a' || 
        username.toLowerCase() === 'anonymous';
      
      if (isAnonymousOrNull) {
        return (
          <VuiTypography variant="button" color="white" fontWeight="regular">
            n/a
          </VuiTypography>
        );
      }
      
      return (
        <Link to={`/dashboards/threats/${item.threatID || 'unknown'}/details`} style={{ textDecoration: 'none', color:"white"}}>
          <VuiTypography
            
            variant="button"
            color="info"
            fontWeight="medium"
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
          {username}
          </VuiTypography>


        </Link>
      );
    })(),

    dateLastActivity: (
      <VuiTypography variant="button" color="white" fontWeight="regular">
        {item.last_activity ? new Date(item.last_activity).toLocaleDateString() : 'N/A'}
      </VuiTypography>
    ),

    // Store raw data for sorting and filtering
    _rawData: item,
  }));

  return {
    columns: [
      { Header: 'TAKEDOWN ID', accessor: 'takedownId', width: '12%' },
      { Header: 'INFRINGEMENT TYPE', accessor: 'infringementType', width: '10%' },
      { Header: 'MEDIA TYPE', accessor: 'mediaType', width: '8%' },
      { Header: 'PRIORITY', accessor: 'priority', width: '10%' },
      { Header: 'PRIORITY REASON', accessor: 'priorityReason', width: '25%' },
      { Header: 'STATUS', accessor: 'status', width: '10%' },
      { Header: 'THREAT ACTOR', accessor: 'threatActor', width: '10%' },
      { Header: 'DATE LAST ACTIVITY', accessor: 'dateLastActivity', width: '10%' },
    ],
    rows: transformedData,
    pagination: pagination,
    filters: filters,
    applied_filters: applied_filters,
    sorting: sorting,
    platform_stats: platform_stats,
  };
};

/**
 * Get filter buttons with counts for the platform dashboard
 * @param {Object} counts - Count data from the API
 * @param {Function} onFilterChange - Callback for filter changes
 * @param {string} activeFilter - Currently active filter
 * @returns {Array} - Array of filter button objects
 */
export const getPlatformFilterButtons = (counts, onFilterChange, activeFilter) => {
  if (!counts?.breakdown) {
    return [
      { key: 'requiresApproval', label: 'Requires Approval', filter: { takedown_status: 'Pending' } },
      { key: 'Explicit', label: 'Explicit', filter: { violation_type: 'Explicit' } },
      { key: 'Copyright', label: 'Copyright', filter: { violation_type: 'Copyright' } }
    ];
  }

  const filterButtons = [];

  // Add status-based filters
  if (counts.breakdown.by_status) {
    const pendingStatus = counts.breakdown.by_status.find(item => item.status === 'Pending');
    if (pendingStatus && pendingStatus.count > 0) {
      filterButtons.push({
        key: 'requiresApproval',
        label: `Requires Approval (${pendingStatus.count})`,
        filter: { takedown_status: 'Pending' },
        count: pendingStatus.count,
      });
    }
  }

  // Add violation type filters
  if (counts.breakdown.by_violation_type) {
    counts.breakdown.by_violation_type.forEach((item) => {
      if (item.count > 0) {
        filterButtons.push({
          key: item.violation_type,
          label: `${item.violation_type} (${item.count})`,
          filter: { violation_type: item.violation_type },
          count: item.count,
        });
      }
    });
  }

  return filterButtons;
};

/**
 * Create empty state for platform dashboard
 * @returns {Object} - Empty DataTable format
 */
export const createEmptyPlatformData = () => ({
  columns: [
    { Header: 'TAKEDOWN ID', accessor: 'takedownId', width: '12%' },
    { Header: 'INFRINGEMENT TYPE', accessor: 'infringementType', width: '10%' },
    { Header: 'MEDIA TYPE', accessor: 'mediaType', width: '8%' },
    { Header: 'PRIORITY', accessor: 'priority', width: '10%' },
    { Header: 'PRIORITY REASON', accessor: 'priorityReason', width: '25%' },
    { Header: 'STATUS', accessor: 'status', width: '10%' },
    { Header: 'THREAT ACTOR', accessor: 'threatActor', width: '10%' },
    { Header: 'DATE LAST ACTIVITY', accessor: 'dateLastActivity', width: '10%' },
  ],
  rows: [],
  pagination: {
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20,
    has_next_page: false,
    has_prev_page: false,
  },
  filters: {
    takedown_status: [],
    violation_type: [],
    media_type: [],
  },
  applied_filters: {
    takedown_status: null,
    violation_type: null,
    media_type: null,
    search: null,
  },
  sorting: {
    sort_by: 'detected_date',
    sort_order: 'DESC',
  },
  platform_stats: {
    total_sent: 0,
    complied: 0,
    uncomplied: 0,
    compliance_rate: 0,
  },
});
